import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, ShoppingBag, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Logo, LogoShield } from "./Logo";
import { useCart } from "../context/CartContext";
import { LANGS } from "../i18n";

const NAV = [
  { key: "homme", labelKey: "nav.man", to: "/boutique?categorie=homme" },
  { key: "femme", labelKey: "nav.woman", to: "/boutique?categorie=femme" },
  { key: "couple", labelKey: "nav.couple", to: "/boutique?categorie=couple" },
  { key: "enfants", labelKey: "nav.kids", to: "/boutique?categorie=enfants" },
  { key: "maroquinerie", labelKey: "nav.leather", to: "/boutique?categorie=maroquinerie" },
  { key: "essayage", labelKey: "nav.essayage", to: "/essayage" },
  { key: "la-maison", labelKey: "nav.maison", to: "/la-maison" },
];

export const LangSwitcher = ({ compact = false }) => {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const current = LANGS.find((l) => l.code === i18n.language) || LANGS[0];

  return (
    <div className="relative">
      <button
        data-testid="lang-switcher-button"
        onClick={() => setOpen(!open)}
        aria-label={t("header.lang")}
        className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-ivoire/80 transition-colors hover:text-or"
      >
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-or">
          <circle cx="12" cy="12" r="10" />
          <path d="M2 12h20M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
        </svg>
        {!compact && current.code.toUpperCase()}
      </button>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18 }}
            className="absolute end-0 top-full z-[70] mt-3 min-w-[160px] border border-ligne bg-noir/95 backdrop-blur-md"
          >
            {LANGS.map((l) => (
              <button
                key={l.code}
                data-testid={`lang-option-${l.code}`}
                onClick={() => { i18n.changeLanguage(l.code); setOpen(false); }}
                className={`flex w-full items-center justify-between px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] transition-colors ${
                  l.code === current.code ? "bg-or/10 text-or" : "text-argile hover:text-or"
                }`}
              >
                {l.native}
                {l.code === current.code && <span className="h-1.5 w-1.5 rotate-45 bg-or" />}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export const Header = () => {
  const { count, openCart } = useCart();
  const { t } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const isAdmin = location.pathname.startsWith("/admin");

  useEffect(() => setMenuOpen(false), [location]);

  return (
    <>
      <header className="glass-header fixed inset-x-0 top-0 z-50">
        <div className="mx-auto max-w-[1500px] px-6 lg:px-12">
          <div className="flex h-24 items-center justify-between lg:h-28">
            <div className="flex flex-1 items-center gap-4 lg:hidden">
              <button
                data-testid="header-menu-button"
                onClick={() => setMenuOpen(true)}
                className="text-ivoire transition-colors hover:text-or"
                aria-label={t("header.openMenu")}
              >
                <Menu size={22} />
              </button>
            </div>
            <div className="hidden flex-1 items-center gap-8 lg:flex">
              {!isAdmin && <LangSwitcher />}
              <button
                data-testid="header-surmesure-button"
                onClick={() => navigate("/la-maison#rendez-vous")}
                className="hidden border border-or/60 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-or transition-all duration-300 hover:bg-or hover:text-noir md:block"
              >
                {t("nav.surmesure")}
              </button>
            </div>

            <Link to="/" data-testid="header-logo-link" className="flex shrink-0 flex-col items-center">
              <LogoShield height={46} />
              <span className="mt-2 font-serif text-lg leading-none tracking-[0.22em] text-ivoire">B.KOSS</span>
              <span className="mt-1.5 font-mono text-[7px] uppercase tracking-[0.32em] text-or">
                {t("header.slogan")}
              </span>
            </Link>

            <div className="flex flex-1 items-center justify-end gap-5">
              <button
                data-testid="header-surmesure-button-mobile"
                onClick={() => navigate("/la-maison#rendez-vous")}
                className="hidden border border-or/60 px-5 py-2.5 font-mono text-[10px] uppercase tracking-[0.25em] text-or transition-all duration-300 hover:bg-or hover:text-noir md:block lg:hidden"
              >
                {t("nav.surmesure")}
              </button>
              <div className="lg:hidden">
                {!isAdmin && <LangSwitcher compact />}
              </div>
              <button
                data-testid="header-cart-button"
                onClick={openCart}
                className="relative text-ivoire transition-colors hover:text-or"
                aria-label={t("header.openCart")}
              >
                <ShoppingBag size={21} strokeWidth={1.5} />
                {count > 0 && (
                  <span
                    data-testid="cart-icon-badge"
                    className="absolute -top-2 end-[-0.5rem] flex h-4 w-4 items-center justify-center bg-or font-mono text-[9px] text-noir"
                  >
                    {count}
                  </span>
                )}
              </button>
            </div>
          </div>

          <nav className="hidden items-center justify-center gap-10 pb-4 lg:flex">
            {NAV.map((n) => (
              <Link
                key={n.key}
                data-testid={`nav-link-${n.key}`}
                to={n.to}
                className="group relative whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.28em] text-ivoire/80 transition-colors hover:text-or"
              >
                {t(n.labelKey)}
                <span className="absolute -bottom-1 start-0 h-px w-0 bg-or transition-all duration-300 group-hover:w-full" />
              </Link>
            ))}
          </nav>
        </div>
      </header>

      <AnimatePresence>
        {menuOpen && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] flex flex-col bg-noir"
          >
            <div className="flex h-20 items-center justify-between px-6">
              <Logo />
              <button
                data-testid="mobile-menu-close-button"
                onClick={() => setMenuOpen(false)}
                className="text-ivoire hover:text-or"
                aria-label={t("header.closeMenu")}
              >
                <X size={26} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {[...NAV, { key: "sur-mesure", labelKey: "nav.surmesure", to: "/la-maison#rendez-vous" }].map((n, i) => (
                <motion.div
                  key={n.key}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                >
                  <Link
                    data-testid={`mobile-menu-link-${n.key}`}
                    to={n.to}
                    className="block py-3 font-serif text-4xl text-ivoire transition-colors hover:text-or"
                  >
                    {t(n.labelKey)}
                  </Link>
                </motion.div>
              ))}
            </nav>
            <div className="flex items-center justify-between px-8 pb-10">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-argile">
                {t("header.city")}
              </p>
              {!isAdmin && <LangSwitcher />}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
